import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FieldLabel, Panel } from "@/admin/ui";
import { ImageField } from "@/admin/ImageField";

const TOKENS = [["{titular}", "Razón social"], ["{nif}", "NIF/CIF"], ["{domicilio}", "Domicilio"], ["{email}", "Correo"], ["{telefono}", "Teléfono"],
  ["{registro}", "Datos registrales"], ["{sitio}", "Nombre del periódico"], ["{web}", "Dirección de la web"],
  ["{proveedores}", "Herramientas de medición y publicidad activas"], ["{cookies_terceros}", "Lista automática de cookies de terceros"]];

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 text-sm border border-border p-3">{label}<Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} /></label>
);

export const PageSettingsPanel = ({ form, patch }) => (
  <Panel title="Publicación y menús" testid="page-settings-panel">
    <div className="grid grid-cols-2 gap-1 p-1 bg-muted">
      {[["published", "Publicada"], ["draft", "Borrador"]].map(([k, label]) => (
        <button key={k} type="button" onClick={() => patch({ status: k })} data-testid={`page-status-${k}`}
          className={`py-2 text-sm transition-colors ${form.status === k ? "bg-card shadow-sm font-semibold" : "text-muted-foreground"}`}>{label}</button>
      ))}
    </div>
    <div className="mt-4 space-y-3">
      <div><FieldLabel hint="en el pie y en la barra superior">Nombre corto en los menús</FieldLabel><Input value={form.nav_label} onChange={(e) => patch({ nav_label: e.target.value })} placeholder="Ej.: Contacto" data-testid="page-nav-label-input" /></div>
      <div>
        <FieldLabel>Enlace en el pie de página</FieldLabel>
        <Select value={form.footer_group} onValueChange={(v) => patch({ footer_group: v })}>
          <SelectTrigger data-testid="page-footer-group-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="main">Columna del periódico</SelectItem>
            <SelectItem value="legal">Columna legal</SelectItem>
            <SelectItem value="none">No mostrar en el pie</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div><FieldLabel hint="menor = antes">Orden en los menús</FieldLabel><Input type="number" min={0} max={999} value={form.order} onChange={(e) => patch({ order: Number(e.target.value) || 0 })} data-testid="page-order-input" /></div>
      <Toggle label="Enlazar en la barra superior" checked={form.in_topbar} onChange={(v) => patch({ in_topbar: v })} testid="page-topbar-switch" />
      <Toggle label="Mostrar «Última actualización»" checked={form.show_updated} onChange={(v) => patch({ show_updated: v })} testid="page-show-updated-switch" />
    </div>
  </Panel>
);

export const PageSeoPanel = ({ form, patch, path, content }) => (
  <Panel title="SEO y buscadores" description="Cómo aparece esta página en Google y en los buscadores con IA." testid="page-seo-panel">
    <div className="border border-border bg-background p-4" data-testid="page-google-preview">
      <p className="text-xs text-emerald-700 dark:text-emerald-400 truncate">{window.location.host}{path}</p>
      <p className="text-[#1a0dab] dark:text-sky-400 text-[17px] leading-snug line-clamp-1 mt-0.5">{form.seo_title || form.title || "Título de la página"}</p>
      <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{form.seo_description || form.lead || "Descripción que verá el lector en los resultados."}</p>
    </div>
    <div className="mt-4 space-y-3">
      <div><FieldLabel hint={`${(form.seo_title || "").length}/60`} warn={(form.seo_title || "").length > 60}>Título SEO</FieldLabel><Input value={form.seo_title || ""} onChange={(e) => patch({ seo_title: e.target.value })} placeholder="Si lo dejas vacío se usa el título" data-testid="page-seo-title-input" /></div>
      <div><FieldLabel hint={`${(form.seo_description || "").length}/155`} warn={(form.seo_description || "").length > 155}>Meta descripción</FieldLabel><Textarea rows={3} value={form.seo_description || ""} onChange={(e) => patch({ seo_description: e.target.value })} placeholder="Si la dejas vacía se usa la entradilla" data-testid="page-seo-description-input" /></div>
      {content && (
        <>
          <div><FieldLabel>Dirección (URL)</FieldLabel><Input value={form.slug || ""} onChange={(e) => patch({ slug: e.target.value })} placeholder="se-genera-sola-desde-el-titulo" data-testid="page-slug-input" /></div>
          <ImageField label="Imagen al compartir" hint="1200×630" value={form.share_image} onChange={(v) => patch({ share_image: v })} testid="page-share-image" />
          <Toggle label="Ocultar esta página en buscadores" checked={form.noindex} onChange={(v) => patch({ noindex: v })} testid="page-noindex-switch" />
        </>
      )}
    </div>
  </Panel>
);

export const TokensPanel = () => (
  <Panel title="Datos automáticos" description="Escribe estas palabras entre llaves en cualquier texto de la página y se sustituyen por los datos de Ajustes." testid="page-tokens-panel">
    <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
      {TOKENS.map(([token, label]) => <li key={token}><code className="font-mono text-primary">{token}</code> <span className="text-muted-foreground">{label}</span></li>)}
    </ul>
    <Link to="/admin/ajustes#titular" className="kicker text-[10px] mt-4 inline-block hover:text-primary" data-testid="page-tokens-settings-link">Editar los datos del titular →</Link>
  </Panel>
);

export const SystemTextsPanel = ({ fields, texts, onChange }) => (
  <Panel title="Textos de la página" description="Todo lo que se lee en esta página. Si dejas un texto vacío, no se muestra." testid="page-texts-panel">
    <div className="space-y-4">
      {fields.map((f) => {
        const value = texts[f.key] ?? f.default;
        const set = (e) => onChange({ ...texts, [f.key]: e.target.value });
        return (
          <div key={f.key}>
            <FieldLabel hint={f.hint}>{f.label}</FieldLabel>
            {f.area ? <Textarea rows={3} value={value} onChange={set} data-testid={`page-text-${f.key}`} /> : <Input value={value} onChange={set} data-testid={`page-text-${f.key}`} />}
            {value !== f.default && <button type="button" onClick={() => onChange({ ...texts, [f.key]: f.default })} className="mt-1 text-xs text-muted-foreground hover:text-primary" data-testid={`page-text-reset-${f.key}`}>Volver al texto original</button>}
          </div>
        );
      })}
    </div>
  </Panel>
);
